import { DisablerStatus, DutyType, type Vehicle } from '@cobliteam/common-types';

import { type DeviceGeneralType, DeviceType } from '../../__generated__/graphql';

export const isDeviceTypeCamera = (deviceType: DeviceType | DeviceGeneralType | string): boolean => {
  if (!deviceType) return false;

  const cameraTypes = [DeviceType.Dashcam.toString().toLowerCase(), DeviceType.Multicam.toString().toLowerCase()];

  const normalizedDeviceType = deviceType.toString().toLowerCase();
  return cameraTypes.includes(normalizedDeviceType);
};

// Permanent rule: a device is a camera if ANY of the API's classifications says camera.
// `type` (granular) is derived from a model→enum map in the BFF that lags behind whenever a
// new hardware family joins the fleet (e.g. ADPLUS arrived as type=Unknown), while
// `generalType` (category) comes correct. Do not "simplify" this to a single field — it
// re-breaks video request for that class of devices. See docs/adr/0008.
export const isCameraDevice = (device: { type?: DeviceType | string | null; generalType?: DeviceGeneralType | string | null }): boolean =>
  isDeviceTypeCamera(device.type ?? '') || isDeviceTypeCamera(device.generalType ?? '');

export const isVehicleCamera = (vehicle: Vehicle): boolean => isCameraDevice({ type: vehicle.deviceType, generalType: vehicle.deviceGeneralType });

export const isDeviceTypeOverSpeedingAlarm = (deviceType: DeviceType | string): boolean => {
  return (
    deviceType === DeviceType.IButton ||
    deviceType === DeviceType.Blocker ||
    deviceType === DeviceType.Immobilizer ||
    deviceType === DeviceType.Buzzer ||
    deviceType === DeviceType.PlugAndPlayBuzzer ||
    deviceType === DeviceType.IButtonImmobilizer ||
    deviceType === DeviceType.CanIbutton ||
    deviceType === DeviceType.CanIbuttonImmobillizer
  );
};

export const isVehicleWithOverSpeedingAlarm = (vehicle: Vehicle): boolean => vehicle.deviceType !== undefined && isDeviceTypeOverSpeedingAlarm(vehicle.deviceType);

export const isImmobilizedVehicle = (vehicle: Vehicle): boolean => {
  if (vehicle.deviceType !== undefined) {
    const deviceType = vehicle.deviceType;
    if (deviceType === DeviceType.Immobilizer) {
      const disablerStatus = Object.values(DisablerStatus).find((status: string) => status === vehicle.disablerStatus?.status);
      return disablerStatus === DisablerStatus.Disabled || disablerStatus === DisablerStatus.Disabling;
    }
  }
  return false;
};

export const getVehicleDutyType = (vehicle: Vehicle): DutyType | undefined => {
  switch (vehicle.size) {
    case 'Pequeno':
      return DutyType.LIGHT;
    case 'Médio':
      return DutyType.MEDIUM;
    case 'Grande':
      return DutyType.HEAVY;
    default:
      return undefined;
  }
};

export const hasSupportToBluetoothRemoteConnection = (model?: string): boolean => {
  return model?.toLowerCase().startsWith('fm') ?? false;
};
